import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import MetaTags from "react-meta-tags";
import { Alert } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import { loginUser } from "../../store/actions";

// Icons from react-icons
import {
  RiToothLine,
  RiCalendarCheckLine,
  RiMedicineBottleLine,
  RiShieldCheckLine,
  RiLockPasswordLine,
  RiMailLine,
  RiEyeLine,
  RiEyeOffLine,
  RiArrowRightLine,
  RiHospitalLine,
  RiUserLine,
} from "react-icons/ri";

import oralstopLogo from "../../assets/images/logos/oralstop-logo.png";

import "./auth-custom.scss";

const Login = (props) => {
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);
  const submitTimerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
    };
  }, []);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const { error } = useSelector((state) => ({
    error: state.Login.error,
  }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (validationError) setValidationError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setValidationError("Please enter your registered email address or username.");
      return;
    }

    if (!formData.password) {
      setValidationError("Please enter your password.");
      return;
    }

    setIsSubmitting(true);
    dispatch(
      loginUser(
        {
          email: formData.email.trim(),
          password: formData.password,
        },
        props.history
      )
    );

    submitTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }, 1200);
  };

  const features = [
    {
      icon: <RiToothLine />,
      title: "Interactive Dental Charting",
      desc: "Adult & pediatric tooth mapping with condition logs",
    },
    {
      icon: <RiCalendarCheckLine />,
      title: "Smart Chair & OPD Scheduling",
      desc: "Multi-chair dental appointment and doctor queue sync",
    },
    {
      icon: <RiMedicineBottleLine />,
      title: "Digital Prescriptions & Rx",
      desc: "One-click dental Rx templates with instant PDF printing",
    },
    {
      icon: <RiHospitalLine />,
      title: "Complete Billing & Ledgers",
      desc: "Treatment pricing, patient dues, expenses & analytics",
    },
  ];

  const [loginType, setLoginType] = useState('staff'); // 'staff' or 'patient'

  return (
    <React.Fragment>
      <MetaTags>
        <title>Sign In | Oralstop OPD - Clinical Management System</title>
      </MetaTags>

      <div className="dental-auth-wrapper">
        <div className="dental-auth-card">
          {/* Left Hero Panel */}
          <div className="dental-hero-panel">
            <div>
              {/* Brand Logo & Header */}
              <div className="dental-brand-header">
                <img src={oralstopLogo} alt="oralstop.com" height="38" className="dental-brand-logo-img" />
                <div>
                  <span className="dental-brand-sub">Clinical EHR Platform</span>
                </div>
              </div>

              {/* Main Heading */}
              <h2 className="dental-hero-heading">
                Next-Generation <span>Dental Practice</span> & Patient Care.
              </h2>
              <p className="dental-hero-desc">
                Role-based access for Doctors, Front-Desk Receptionists, Clinic Administrators, and Patients with 256-bit HIPAA compliance.
              </p>

              {/* Clinical Feature List */}
              <div className="dental-features-list">
                {features.map((feat, idx) => (
                  <div key={idx} className="dental-feature-item">
                    <div className="dental-feature-icon">{feat.icon}</div>
                    <div className="dental-feature-text">
                      <h6>{feat.title}</h6>
                      <p>{feat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Badge */}
            <div className="dental-security-badge">
              <span className="pulse-dot" />
              <RiShieldCheckLine className="me-1" />
              <span>Multi-Role RBAC & 256-bit AES Encrypted</span>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="dental-form-panel">
            <div className="dental-form-container">
              {/* Login Type Switcher (Staff vs Patient) */}
              <div className="btn-group w-100 p-1 bg-light rounded-pill border mb-3">
                <button
                  type="button"
                  className={`btn btn-sm ${loginType === 'staff' ? 'btn-primary font-weight-bold shadow-xs' : 'btn-light text-muted'} rounded-pill py-1.5 font-size-12`}
                  onClick={() => setLoginType('staff')}
                >
                  <RiHospitalLine className="me-1" /> Staff / Doctor / Admin
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${loginType === 'patient' ? 'btn-primary font-weight-bold shadow-xs' : 'btn-light text-muted'} rounded-pill py-1.5 font-size-12`}
                  onClick={() => setLoginType('patient')}
                >
                  <RiUserLine className="me-1" /> Patient Portal
                </button>
              </div>

              <div className="dental-form-header">
                <h3 className="dental-form-title">
                  {loginType === 'staff' ? 'Clinical Staff Sign In' : 'Patient Portal Access'}
                </h3>
                <p className="dental-form-subtitle">
                  {loginType === 'staff'
                    ? 'Sign in as Doctor, Receptionist, or Clinic Administrator.'
                    : 'View your scheduled appointments, treatment plans, Rx, and invoices.'}
                </p>
              </div>

              {/* Error Alerts */}
              {(error || validationError) && (
                <Alert color="danger" className="mb-3 d-flex align-items-center py-2 px-3">
                  <i className="mdi mdi-alert-circle-outline me-2 font-size-16" />
                  <div className="font-size-13">{error || validationError}</div>
                </Alert>
              )}

              {loginType === 'patient' && (
                <div className="alert alert-info py-2 px-3 mb-3 font-size-12 d-flex align-items-center justify-content-between">
                  <span>Want to book an appointment?</span>
                  <Link to="/clinic/demo-clinic" className="font-weight-bold text-primary text-decoration-underline">
                    Public Booking &rarr;
                  </Link>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Email / Username Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="email-input">
                    Email or Username
                  </label>
                  <div className="custom-input-box">
                    <RiMailLine className="input-icon-prefix" />
                    <input
                      id="email-input"
                      name="email"
                      type="text"
                      className="form-control dental-input-field"
                      placeholder="doctor@dentalopd.com"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="username"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="password-input">
                    Password
                  </label>
                  <div className="custom-input-box">
                    <RiLockPasswordLine className="input-icon-prefix" />
                    <input
                      id="password-input"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className="form-control dental-input-field"
                      placeholder="••••••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="form-options-row">
                  <label className="custom-checkbox-label">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      className="custom-checkbox-input"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    <span>Remember my session</span>
                  </label>

                  <Link to="/forgot-password" className="forgot-link">
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="dental-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span>Signing in...</span>
                  ) : (
                    <>
                      <span>Sign In to Clinical OPD</span>
                      <RiArrowRightLine className="font-size-16" />
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="auth-footer-text">
                <span>Need a new clinic workspace?</span>
                <Link to="/register" className="signup-link">
                  Register Clinic
                </Link>
                <div className="mt-2 text-muted font-size-11">
                  © {new Date().getFullYear()}Oralstop OPD · All Rights Reserved
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

Login.propTypes = {
  history: PropTypes.object,
};

export default withRouter(Login);
